/**
 * Sidebar Usage Tracker - Compact quota display for Results sidebar
 *
 * Shows total comparisons stored vs limit on Responses, Scores, and Consensus tabs.
 * - Expanded: Full display with progress bar and percentage
 * - Collapsed: Compact "X/Y" display in single row
 */

import React, { useState, useEffect } from 'react';
import { entitlementService } from '../../../services/entitlement';

interface SidebarUsageTrackerProps {
  collapsed?: boolean;
}

interface QuotaData {
  count: number;          // Total comparisons stored
  limit: number;          // Max storage limit from plan
  remaining: number;      // Remaining slots
  percentage: number;     // Percentage used
  allowed: boolean;       // Whether user can store more
  planDisplayName?: string;
  progressColor: string;
  upgradeUrl?: string;
}

export const SidebarUsageTracker: React.FC<SidebarUsageTrackerProps> = ({ collapsed = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [quotaData, setQuotaData] = useState<QuotaData | null>(null);

  useEffect(() => {
    const fetchQuotaStatus = async () => {
      try {
        setIsLoading(true);
        const status = await entitlementService.getStatus();

        // If paywall is disabled or no quota data, don't show tracker
        if (!status.paywallEnabled || !status.plan || !status.quotas) {
          setQuotaData(null);
          return;
        }

        // Use savedComparisons quota (total storage) instead of daily comparisons
        const savedQuota = status.quotas.savedComparisons;

        // If no savedComparisons quota or unlimited (-1), don't show tracker
        if (!savedQuota || savedQuota.limit === -1) {
          setQuotaData(null);
          return;
        }

        // Determine color based on percentage
        const percentage = savedQuota.percentage;
        const progressColor =
          percentage >= 100 ? 'bg-red-500' :
          percentage >= 80 ? 'bg-amber-500' :
          percentage >= 50 ? 'bg-yellow-500' :
          'bg-green-500';

        setQuotaData({
          count: savedQuota.count,
          limit: savedQuota.limit,
          remaining: savedQuota.remaining,
          percentage: savedQuota.percentage,
          allowed: savedQuota.allowed,
          planDisplayName: status.plan.displayName,
          progressColor,
          upgradeUrl: status.upgradeUrl
        });
      } catch (error) {
        console.error('[SidebarUsageTracker] Failed to fetch quota:', error);
        setQuotaData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuotaStatus();
  }, []);

  // Don't render if loading or no quota data
  if (isLoading || !quotaData) {
    return null;
  }

  // Collapsed state: compact single-row display
  if (collapsed) {
    return (
      <div className="flex flex-col items-center py-3">
        <div className="text-xs font-medium text-slate-300 whitespace-nowrap">
          {quotaData.count}/{quotaData.limit}
        </div>
      </div>
    );
  }

  // Expanded state: full display with progress bar
  return (
    <div className="px-3 sm:px-4 py-3 sm:py-4 border-t border-slate-700/50">
      <h3 className="text-xs sm:text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2 sm:mb-3">
        Total Comparisons
      </h3>

      <div className="space-y-2">
        {/* Storage status with percentage */}
        <div className="text-sm text-slate-300">
          <span className="font-medium">{quotaData.count}</span> of{' '}
          <span className="font-medium">{quotaData.limit}</span> stored
          <span className="text-slate-400 ml-1">({quotaData.percentage}%)</span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${quotaData.progressColor}`}
            style={{ width: `${Math.min(quotaData.percentage, 100)}%` }}
          />
        </div>

        {/* Remaining slots */}
        <div className="text-xs text-slate-400">
          {quotaData.remaining} {quotaData.remaining === 1 ? 'slot' : 'slots'} remaining
        </div>

        {/* Upgrade link */}
        {quotaData.upgradeUrl && (quotaData.percentage >= 60 || quotaData.remaining <= 2) && (
          <div className="flex justify-end">
            <a
              href={quotaData.upgradeUrl}
              className="text-xs text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
            >
              Upgrade →
            </a>
          </div>
        )}

        {/* Limit reached warning */}
        {!quotaData.allowed && quotaData.percentage >= 100 && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-2 text-xs">
            <div className="text-red-400 font-semibold mb-1">Storage limit reached</div>
            <div className="text-red-300">
              <a
                href={quotaData.upgradeUrl || '/auth/signin'}
                className="underline hover:text-red-200"
              >
                Upgrade for more storage
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
